import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation, OnInit, ContentChild, TemplateRef, NgZone, inject } from '@angular/core';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { CommonIP } from '@shared/model/ftf-api.model';
import { Observable, BehaviorSubject } from 'rxjs';

import { IpIconComponent } from '../ip-icon/ip-icon.component';


@Component({
  selector: 'app-paginated-autocomplete',
  templateUrl: './paginated-autocomplete.component.html',
  styleUrls: ['./paginated-autocomplete.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatAutocomplete,
    MatOption,
    NgTemplateOutlet,
    MatCheckbox,
    IpIconComponent,
    MatIconButton,
    MatIcon,
    AsyncPipe
  ],
})
/**
 * The `UploaderComponent` is responsible for managing the file upload process,
 * including handling user interactions, managing file data, and communicating
 * with the backend API. It supports uploading files to different IP types,
 * managing pagination, and handling file-related metadata like keywords and
 * document notes.
 *
 * Features:
 * - File upload with progress tracking
 * - Support for different IP types and document subtypes
 * - Integration with Office add-ins and standalone environments
 * - Pagination and search functionality for IP items
 * - Validation for file selection
 * - Communication with parent windows or Office dialogs
 *
 * Lifecycle Hooks:
 * - `ngOnInit`: Initializes the component, sets up subscriptions, and loads user preferences.
 * - `ngAfterViewInit`: Sets up message listeners for parent window communication.
 * - `ngOnDestroy`: Cleans up subscriptions and resources.
 *
 * Dependencies:
 * - Angular services for API calls, form building, and change detection
 * - RxJS for reactive programming
 * - Angular Material components for UI interactions
 */
export class PaginatedAutocompleteComponent implements OnInit {
  private ngZone = inject(NgZone);

  private nextButtonDisabled = false;
  private previousButtonDisabled = false;
  @Input() searchItems$!: Observable<CommonIP[]>;
  @Input() displayFn!: (value: string) => string;
  @Input() orientation: string = '';
  @Input() paginationInfo$!: Observable<{ currentPage: number; totalPages: number; totalRows?: number; resultLength?: number }>;
  @Input() showCheckboxes = false;
  @Input() isAll = false;
  @Input() selectedItems: CommonIP[] = [];
  @Input() isInUploaderComponent = false;
  @Output() optionSelected = new EventEmitter<MatAutocompleteSelectedEvent>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() checkboxChange = new EventEmitter<{ event: MatCheckboxChange; item: CommonIP }>();
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('auto') matAutocomplete!: MatAutocomplete;
  @ContentChild(TemplateRef) customTemplate: TemplateRef<any> | null = null; //TODO might use never here

  paginationInfo = new BehaviorSubject<{ currentPage: number; totalPages: number; totalRows?: number; resultLength?: number }>({
    currentPage: 1,
    totalPages: 1,
    totalRows: 1,
    resultLength: 1,
  });
  private manualPageChange = false;

  /**
   * Lifecycle hook that initializes the component.
   * Sets up subscription to pagination information from parent component.
   * Prevents UI from reverting to page 1 when manual page changes occur.
   */
  ngOnInit() {
    if (this.paginationInfo$) {
      this.paginationInfo$.subscribe((info) => {
        // Only update from the parent if we haven't just manually changed pages
        // This prevents the UI from reverting to page 1 when new data loads
        if (!this.manualPageChange) {
          this.paginationInfo.next(info);
        }
        this.manualPageChange = false;
      });
    }
  }

  /**
   * Resets the pagination information to its initial state.
   */
  public reset(): void {
    this.paginationInfo.next({
      currentPage: 1,
      totalPages: 1,
      totalRows: 0,
      resultLength: 0,
    });
    this.manualPageChange = false;
    if (this.autocompleteTrigger) {
      this.autocompleteTrigger.closePanel();
    }
  }

  /**
   * Handles selection change in the autocomplete dropdown.
   * Emits the selected event to the parent component.
   *
   * @param event - The autocomplete selection event containing the selected item
   */
  onSelectionChange(event: MatAutocompleteSelectedEvent) {
    this.optionSelected.emit(event);
  }

  /**
   * Navigates to the previous page of results.
   * Temporarily disables the button to prevent multiple rapid clicks.
   * Updates pagination info locally and emits the page change event.
   *
   * @param event - Optional mouse event from button click
   */
  previousPage(event?: MouseEvent): void {
    if (this.paginationInfo.value.currentPage > 1 && !this.previousButtonDisabled) {
      this.previousButtonDisabled = true;
      if (event?.target) {
        const button = (event.target as Element).closest('button');
        if (button) button.setAttribute('disabled', 'true');
      }
      const prevPageIndex = this.paginationInfo.value.currentPage - 2;
      this.manualPageChange = true;
      this.paginationInfo.next({
        currentPage: this.paginationInfo.value.currentPage - 1,
        totalPages: this.paginationInfo.value.totalPages,
      });
      this.ngZone.run(() => {
        this.pageChange.emit(prevPageIndex);
      });

      setTimeout(() => {
        this.previousButtonDisabled = false;
        if (event?.target) {
          const button = (event.target as Element).closest('button');
          if (button) button.removeAttribute('disabled');
        }
      }, 2000);
    }
  }

  /**
   * Navigates to the next page of results.
   * Temporarily disables the button to prevent multiple rapid clicks.
   * Updates pagination info locally and emits the page change event.
   *
   * @param event - Optional mouse event from button click
   */
  nextPage(event?: MouseEvent): void {
    if (this.paginationInfo.value.currentPage < this.paginationInfo.value.totalPages && !this.nextButtonDisabled) {
      this.nextButtonDisabled = true;
      if (event?.target) {
        const button = (event.target as Element).closest('button');
        if (button) button.setAttribute('disabled', 'true');
      }
      const nextPageIndex = this.paginationInfo.value.currentPage;
      this.manualPageChange = true;
      this.paginationInfo.next({
        currentPage: this.paginationInfo.value.currentPage + 1,
        totalPages: this.paginationInfo.value.totalPages,
      });
      this.ngZone.run(() => {
        this.pageChange.emit(nextPageIndex);
      });

      setTimeout(() => {
        this.nextButtonDisabled = false;
        if (event?.target) {
          const button = (event.target as Element).closest('button');
          if (button) button.removeAttribute('disabled');
        }
      }, 2000);
    }
  }

  /**
   * Handles checkbox state changes for items when checkboxes are enabled.
   * Emits the checkbox change event along with the associated item.
   *
   * @param event - The checkbox change event
   * @param item - The item associated with the checkbox
   */
  onCheckboxChange(event: MatCheckboxChange, item: CommonIP): void {
    if (this.showCheckboxes) {
      this.checkboxChange.emit({ event, item });
    }
  }

  /**
   * Determines if an item is currently in the selected items array.
   * Compares both identifier and ipType properties.
   *
   * @param item - The item to check
   * @returns True if the item is selected, false otherwise
   */
  isItemSelected(item: CommonIP): boolean {
    return this.selectedItems.some((selectedItem) => selectedItem.identifier === item.identifier && selectedItem.ipType === item.ipType);
  }

  /**
   * Prevents the autocomplete panel from closing when interacting with elements inside it.
   *
   * @param event - The mouse event to prevent default behavior
   */
  preventClose(event: MouseEvent): void {
    event.preventDefault();
  }

  /**
   * Calculates the appropriate width for the autocomplete panel.
   * Returns different widths based on component context and orientation.
   *
   * @returns A CSS width value as a string
   */
  get panelWidth(): string {
    return this.isInUploaderComponent ? 'calc(100% - 475px)' : this.orientation === 'Horizontal' ? '348px' : 'calc(100% - 30px)';
  }
}
